import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Globals } from 'app/_globals';

@Component({
  selector: 'scheduler',
  templateUrl: './scheduler.component.html'
})
export class SchedulerComponent implements OnInit {
  @Input() isWorker: boolean = false;
  @Input() siteId: number;
  @Input() assetsAndSchedules = [];
  @Input() start: Date;
  @Input() end: Date;
  @Input() hoursPerColumn: number = 1;
  @Input() subdivisionsPerColumn: number = 6;

  @Output() gridClick = new EventEmitter<any>();
  @Output() bookingClick = new EventEmitter<any>();

  public hours: { label: string, date: Date }[] = [];
  public days: string[];
  public bookings: any[] = [];
  public numberOfMainColumns: number;
  public numberofMainRows: number;
  public routeRole: string;

  constructor(
    globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;
  }

  ngOnInit() {
    this.getTimeHeaders(this.start, this.end, this.hoursPerColumn);

    this.numberOfMainColumns = this.hours.length;
    this.numberofMainRows = this.assetsAndSchedules.length;

    this.bookings = [];
    const headingRowsOffset = 3;
    this.assetsAndSchedules.forEach((asset, i) => {
      asset.assetAvailability.forEach(booking => {
        const columnPosition = this.calculateColumnPosition(this.start, this.end, new Date(booking.startTime), new Date(booking.endTime), this.hoursPerColumn, this.subdivisionsPerColumn);
        let statusExtraClasses = booking.status === 'PENDING' ? ' pending' : '';
        statusExtraClasses = booking.status === 'CANCELLED' ? ' cancelled' : statusExtraClasses;

        this.bookings.push({
          booking: booking,
          assetId: asset.id,
          graphDetails: {
            columnStart: columnPosition.columnStart,
            columnSpan: columnPosition.columnSpan,
            rowStart: i + headingRowsOffset,
            rowSpan: 1,
            extraClasses: columnPosition.extraClasses + statusExtraClasses,
          }
        });
      });
    });
  }

  private getTimeHeaders(start: Date, end: Date, hoursPerColumn: number) {
    const days = [];
    const hours = [];
    let time = new Date(start);
    let previousDay = '';

    while (time < end) {
      const day = time.toLocaleDateString('en-GB', { weekday: 'long' }) + ' ' + time.getDate();
      const hour = time.getHours().toString();

      if (day !== previousDay) {
        days.push(day);
        previousDay = day;
      } else {
        days.push('');
      }

      const formattedHour = hour.length === 1 ? '0' + hour : hour;
      hours.push({ label: formattedHour, date: new Date(time) });

      time.setHours(time.getHours() + hoursPerColumn);
    }

    this.hours = hours;
    this.days = days;
  }

  private calculateColumnPosition(rangeStart: Date, rangeEnd: Date, bookingStartTime: Date, bookingEndTime: Date, hoursPerColumn: number, subdivisionsPerColumn: number) {
    let extraClasses = "";
    const gridStartingIndex = (subdivisionsPerColumn * hoursPerColumn) + 1; // starting after the asset label column
    const smallGridIncrementSize = (60 / subdivisionsPerColumn); // subdivision 6 = 10 minutes
    const squaresInFullGrid = gridStartingIndex + Math.round(this.timeDiffernceInMinutes(rangeEnd, rangeStart) / smallGridIncrementSize);

    // calculate the starting point and duration in minutes
    const minutesFromGridStartToBookingStart: number = this.timeDiffernceInMinutes(bookingStartTime, rangeStart);

    let gridSquaresToBookingStart;
    let gridSquaresToBookingEnd;

    if (bookingStartTime.getTime() > rangeEnd.getTime() || bookingEndTime.getTime() < rangeStart.getTime()) {
      // shouldn't be shown
      return { columnStart: 1, columnSpan: 1, extraClasses: 'hidden' };
    }

    if (bookingStartTime.getTime() < rangeStart.getTime()) {
      gridSquaresToBookingStart = 0;
      extraClasses += ' starts-earlier';
      bookingStartTime = rangeStart; // shorten for end time calculation
    } else {
      gridSquaresToBookingStart = Math.round(minutesFromGridStartToBookingStart / smallGridIncrementSize);
    }

    const bookingLengthInMinutes: number = this.timeDiffernceInMinutes(bookingEndTime, bookingStartTime);
    if (bookingEndTime.getTime() > rangeEnd.getTime()) {
      gridSquaresToBookingEnd = squaresInFullGrid - gridSquaresToBookingStart;
      extraClasses += ' ends-later';
    } else {
      gridSquaresToBookingEnd = Math.round(bookingLengthInMinutes / smallGridIncrementSize);
    }

    const columnStart = gridStartingIndex + gridSquaresToBookingStart;
    const columnSpan = gridSquaresToBookingEnd;

    return { columnStart, columnSpan, extraClasses };
  }

  public calcTop(displayOrder: number): string {
    const topValue = `calc(5 * ${displayOrder})`;
    console.log('Top Value:', topValue);
    return topValue;
  }

  private timeDiffernceInMinutes(end: Date, start: Date): number {
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  }

  public onGridClick(assetId, date) {
    this.gridClick.emit({ assetId, date });
  }

  public onBookingClick(booking) {
    this.bookingClick.emit({ assetId: booking.assetId, booking: booking.booking});
  }
}
